import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { getPostBySlug, getAllPosts } from '../content/blog/registry';
import { useSeo } from '../lib/useSeo';

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const allPosts = getAllPosts();

  useSeo({
    title: post ? `${post.meta.title} - DAIEN` : 'Nie znaleziono wpisu - DAIEN',
    description: post?.meta.excerpt ?? 'Ten wpis nie istnieje.',
    path: post ? `/blog/${post.meta.slug}` : '/blog',
    image: post?.meta.cover,
    noindex: !post,
    ogType: post ? 'article' : 'website',
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    const created: HTMLScriptElement[] = [];

    const addJsonLd = (id: string, data: unknown) => {
      const s = document.createElement('script');
      s.type = 'application/ld+json';
      s.id = id;
      s.textContent = JSON.stringify(data);
      document.head.appendChild(s);
      created.push(s);
    };

    if (post) {
      const url = `https://daien.pl/blog/${post.meta.slug}`;
      const img = post.meta.cover
        ? `https://daien.pl${post.meta.cover}`
        : 'https://daien.pl/og-image.jpg';

      // BlogPosting JSON-LD - sygnal "to jest prawdziwy artykul" dla Google + AI Overviews.
      addJsonLd('article-schema', {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        mainEntityOfPage: { '@type': 'WebPage', '@id': url },
        headline: post.meta.title,
        description: post.meta.excerpt,
        image: img,
        datePublished: post.meta.date,
        dateModified: post.meta.date,
        keywords: post.meta.tags.join(', '),
        inLanguage: 'pl-PL',
        author: { '@type': 'Person', name: 'Dawid Stępień', url: 'https://daien.pl' },
        publisher: {
          '@type': 'Organization',
          name: 'DAIEN',
          logo: { '@type': 'ImageObject', url: 'https://daien.pl/logoDaien.svg' },
        },
      });

      // FAQPage JSON-LD - strony z nim sa 3.2x czesciej cytowane w AI Overviews.
      if (post.meta.faq?.length) {
        addJsonLd('faq-schema', {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.meta.faq.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        });
      }
    }

    return () => {
      created.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const { meta, Body } = post;
  const otherPosts = allPosts.filter(p => p.slug !== meta.slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-bg text-text-primary">
      <header className="px-6 md:px-10 lg:px-16 pt-10 pb-6 max-w-3xl mx-auto">
        <Link to="/blog" className="text-sm text-muted hover:text-text-primary transition-colors inline-flex items-center gap-2">
          ← Blog
        </Link>
      </header>

      <article className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
        >
          <div className="flex items-center gap-3 mb-6 mt-8 text-xs text-muted">
            <time>{formatDate(meta.date)}</time>
            <span className="w-1 h-1 rounded-full bg-stroke"></span>
            <span>{meta.readTime} min czytania</span>
            <span className="w-1 h-1 rounded-full bg-stroke"></span>
            <div className="flex gap-2">
              {meta.tags.map(tag => (
                <span key={tag} className="px-2 py-0.5 bg-surface rounded-full uppercase tracking-wider">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light mb-8 leading-[1.1]">
            {meta.title}
          </h1>

          <p className="text-xl text-muted mb-12 leading-relaxed">
            {meta.excerpt}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="prose-blog"
        >
          <Body />
        </motion.div>
      </article>

      {/* CTA - umow rozmowe (pod kazdym wpisem) */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 pb-16">
        <div className="rounded-3xl border border-stroke bg-surface p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-light text-text-primary mb-3">
            Chcesz to wdrożyć u siebie?
          </h2>
          <p className="text-muted mb-7 max-w-xl mx-auto">
            Umów bezpłatną 20-minutową rozmowę i zobacz, co da się zautomatyzować w Twojej firmie.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="https://calendar.app.google/4V6jKtrVUtMihmoE9"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-text-primary text-bg px-7 py-3.5 rounded-full font-medium hover:scale-[1.02] transition-transform"
            >
              Umów 20-min rozmowę
              <ArrowRight className="w-4 h-4" />
            </a>
            <Link
              to="/quiz"
              className="inline-flex items-center justify-center border border-stroke text-text-primary px-7 py-3.5 rounded-full font-medium hover:bg-bg transition-colors"
            >
              Wypełnij quiz
            </Link>
          </div>
        </div>
      </section>

      {otherPosts.length > 0 && (
        <section className="max-w-3xl mx-auto px-6 md:px-10 lg:px-16 pb-24 border-t border-stroke pt-16">
          <div className="flex items-center gap-4 mb-8">
            <span className="w-8 h-px bg-stroke"></span>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Czytaj dalej</span>
          </div>
          <div className="space-y-4">
            {otherPosts.map(p => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="block p-6 rounded-2xl hover:bg-surface transition-colors border border-transparent hover:border-stroke"
              >
                <h3 className="text-lg font-light mb-1 hover:text-accent-to transition-colors">{p.title}</h3>
                <p className="text-sm text-muted line-clamp-2">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('pl-PL', { day: '2-digit', month: 'long', year: 'numeric' });
}
